import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractPlainText(value: string) {
  return value
    .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_>#-]/g, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildExcerpt(value: string, maxLength = 160) {
  const text = extractPlainText(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

export function formatDateTimeInput(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (input: number) => input.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function parseTagInput(value: string) {
  return value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export interface ParsedPlannerCommand {
  title: string;
  dueDate: string | null;
  estimatedMinutes: number;
  priority: 'low' | 'medium' | 'high';
}

export function parsePlannerCommand(input: string): ParsedPlannerCommand {
  const raw = input.trim();
  const normalized = raw.toLowerCase();
  const now = new Date();

  let dueDate: string | null = null;
  if (normalized.includes('today')) {
    dueDate = now.toISOString().slice(0, 10);
  } else if (normalized.includes('tomorrow')) {
    const next = new Date(now);
    next.setDate(now.getDate() + 1);
    dueDate = next.toISOString().slice(0, 10);
  }

  const minutesMatch = normalized.match(/(\d+)\s*(m|min|mins|minute|minutes|h|hr|hrs|hour|hours)\b/);
  let estimatedMinutes = 25;
  if (minutesMatch) {
    const value = Number(minutesMatch[1]);
    const unit = minutesMatch[2];
    estimatedMinutes = /h|hr|hrs|hour|hours/.test(unit) ? value * 60 : value;
  }

  const priority: 'low' | 'medium' | 'high' =
    normalized.includes('urgent') || normalized.includes('high priority')
      ? 'high'
      : normalized.includes('low priority') || normalized.includes('light')
        ? 'low'
        : 'medium';

  let title = raw
    .replace(/\b(today|tomorrow)\b/gi, '')
    .replace(/\b(urgent|high priority|low priority|light)\b/gi, '')
    .replace(/\b\d+\s*(m|min|mins|minute|minutes|h|hr|hrs|hour|hours)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!title) {
    title = raw;
  }

  return {
    title,
    dueDate,
    estimatedMinutes,
    priority,
  };
}
